import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFiltersStore, getActiveFilterCount } from '@/store/filtersStore';
import { useUserStore } from '@/store/userStore';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { CalendarIcon, X, SlidersHorizontal, Search, ChevronsUpDown, RefreshCw } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { fetchSyncStatus } from '@/services/api';

const API = process.env.REACT_APP_BACKEND_URL;

const authFetcher = (path) => () => {
  const token = localStorage.getItem('valoriza_token');
  return axios.get(`${API}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  }).then(r => r.data);
};
const fetcher = (path) => () => axios.get(`${API}${path}`).then(r => r.data);

// Multi-select searchable combobox filter
const SearchableFilter = ({ value, field, placeholder, options, testId, onSet, openKey, openFilter, setOpenFilter }) => {
  const isOpen = openFilter === openKey;
  const [search, setSearch] = useState('');

  // Support comma-separated multi-values
  const selected = useMemo(() => (value ? value.split(',').filter(Boolean) : []), [value]);

  const normalizedOptions = useMemo(() =>
    options.map((opt) => ({ value: opt?.value ?? opt, label: opt?.label ?? opt })),
    [options]);

  const filtered = useMemo(() => {
    if (!search) return normalizedOptions;
    const s = search.toLowerCase();
    return normalizedOptions.filter((o) => o.label.toLowerCase().includes(s));
  }, [normalizedOptions, search]);

  const toggle = (optValue) => {
    const isSelected = selected.includes(optValue);
    const next = isSelected ? selected.filter((v) => v !== optValue) : [...selected, optValue];
    onSet(field, next.join(',') || null);
  };

  const triggerLabel = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? (normalizedOptions.find((o) => o.value === selected[0])?.label || selected[0])
      : `${placeholder} (${selected.length})`;

  const isActive = selected.length > 0;

  return (
    <Popover open={isOpen} onOpenChange={(v) => { setOpenFilter(v ? openKey : null); if (!v) setSearch(''); }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={isOpen}
          className="h-7 gap-1.5 text-xs font-normal px-3 min-w-[100px] max-w-[190px] justify-between"
          style={{
            background: isActive ? 'rgba(0,200,83,0.1)' : 'rgba(74,158,255,0.06)',
            border: `1px solid ${isActive ? 'rgba(0,200,83,0.3)' : 'rgba(74,158,255,0.2)'}`,
            color: isActive ? '#00C853' : 'var(--vz-text-secondary)',
          }}
          data-testid={testId}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown size={10} className="flex-shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[230px] p-0"
        style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)', zIndex: 100 }}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={`Buscar ${placeholder.toLowerCase()}...`}
            className="h-8 text-xs"
            style={{ color: 'var(--vz-text-primary)' }}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList style={{ maxHeight: '240px' }}>
            <CommandEmpty className="text-xs py-3 text-center" style={{ color: 'var(--vz-text-muted)' }}>Sin resultados</CommandEmpty>
            <CommandGroup>
              {isActive && (
                <CommandItem
                  onSelect={() => { onSet(field, null); setSearch(''); }}
                  className="text-xs cursor-pointer text-rose-400"
                >
                  <X size={10} className="mr-1.5" /> Limpiar selección
                </CommandItem>
              )}
              {filtered.map((opt) => {
                const isChecked = selected.includes(opt.value);
                return (
                  <CommandItem
                    key={opt.value}
                    value={opt.value}
                    onSelect={() => toggle(opt.value)}
                    className="text-xs cursor-pointer gap-2"
                    style={{ color: 'var(--vz-text-secondary)' }}
                  >
                    <Checkbox
                      checked={isChecked}
                      className="h-3.5 w-3.5 shrink-0"
                      onCheckedChange={() => toggle(opt.value)}
                    />
                    <span className="truncate">{opt.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const GlobalFilterBar = ({ embedded = false }) => {
  const { desde, hasta, sede, negocio, gerente, categoria, centro, persona, incumplimiento, delegacion, setFilter, clearFilters } = useFiltersStore();
  const activeCount = useFiltersStore(getActiveFilterCount);
  const token = useUserStore(s => s.token);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [showExtra, setShowExtra] = useState(false);
  const [openFilter, setOpenFilter] = useState(null);
  const [range, setRange] = useState({
    from: desde ? parseISO(desde) : undefined,
    to: hasta ? parseISO(hasta) : undefined,
  });

  const enabled = !!token;
  // 31/08 #2: toggling the year chip mid-ETL-sync can transiently show
  // different totals for the same year (records get upserted with new/updated
  // fields while a batch is in flight). Surface it explicitly instead of
  // silently showing numbers that look "wrong"/non-deterministic.
  const { data: syncStatus } = useQuery({
    queryKey: ['sync-status-badge'], queryFn: fetchSyncStatus, enabled,
    refetchInterval: 30000, staleTime: 15000,
  });
  const isSyncing = syncStatus?.status === 'running';
  const { data: sedes = [] } = useQuery({ queryKey: ['sedes'], queryFn: fetcher('/api/filters/sedes'), staleTime: 0, enabled });
  const { data: negocios = [] } = useQuery({ queryKey: ['negocios'], queryFn: authFetcher('/api/filters/negocios'), staleTime: 300000, enabled });
  const { data: categorias = [] } = useQuery({ queryKey: ['categorias'], queryFn: authFetcher('/api/filters/categorias'), staleTime: 300000, enabled });
  const { data: gerentes = [] } = useQuery({ queryKey: ['gerentes'], queryFn: authFetcher('/api/filters/gerentes'), staleTime: 300000, enabled });
  const { data: centros = [] } = useQuery({ queryKey: ['centros-filter'], queryFn: authFetcher('/api/filters/centros?capa=ops'), staleTime: 300000, enabled });
  const { data: delegaciones = [] } = useQuery({ queryKey: ['delegaciones'], queryFn: authFetcher('/api/filters/delegaciones'), staleTime: 300000, enabled });

  const handleDateSelect = (r) => {
    setRange(r);
    if (r?.from) setFilter('desde', format(r.from, 'yyyy-MM-dd'));
    if (r?.to) {
      setFilter('hasta', format(r.to, 'yyyy-MM-dd'));
      setCalendarOpen(false);
    }
  };

  const formatDateRange = () => {
    if (desde && hasta) return `${format(parseISO(desde), 'dd/MM/yy')} - ${format(parseISO(hasta), 'dd/MM/yy')}`;
    if (desde) return `Desde ${format(parseISO(desde), 'dd/MM/yy')}`;
    return 'Fecha';
  };

  const YEAR_OPTS = [
    { label: '2024', desde: '2024-01-01', hasta: '2024-12-31' },
    { label: '2025', desde: '2025-01-01', hasta: '2025-12-31' },
    { label: '2026', desde: '2026-01-01', hasta: '2026-12-31' },
  ];

  const activeYear = YEAR_OPTS.find(y => y.desde === desde && y.hasta === hasta);
  const isCustomRange = !activeYear && (desde || hasta);

  const selectYear = (y) => {
    if (activeYear?.label === y.label) {
      setFilter('desde', null); setFilter('hasta', null); setRange({});
    } else {
      setFilter('desde', y.desde); setFilter('hasta', y.hasta);
      setRange({ from: parseISO(y.desde), to: parseISO(y.hasta) });
    }
  };

  const filterProps = { onSet: setFilter, openFilter, setOpenFilter };

  return (
    <div
      className={embedded ? 'space-y-3' : 'px-3 lg:px-6 py-2.5 border-b'}
      style={embedded ? {} : { background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)' }}
      data-testid={embedded ? 'global-filter-bar-embedded' : 'global-filter-bar'}
    >
      {/* ⚠️ Warning banner: custom date range active */}
      {isCustomRange && !embedded && (
        <div
          className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg text-xs mb-1"
          style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.35)', color: '#F59E0B' }}
          data-testid="custom-range-warning"
        >
          <span>
            ⚠ <strong>Rango personalizado activo:</strong> {desde && format(parseISO(desde), 'dd/MM/yyyy')} → {hasta && format(parseISO(hasta), 'dd/MM/yyyy')} — si otros usuarios tienen distinto período, verán datos diferentes.
          </span>
          <button
            className="font-semibold text-[11px] underline whitespace-nowrap"
            onClick={() => {
              const y = YEAR_OPTS.find(o => o.label === String(new Date().getFullYear())) || YEAR_OPTS[YEAR_OPTS.length - 1];
              setFilter('desde', y.desde); setFilter('hasta', y.hasta);
              setRange({ from: parseISO(y.desde), to: parseISO(y.hasta) });
            }}
            data-testid="reset-to-year-btn"
          >
            Volver al año {new Date().getFullYear()}
          </button>
        </div>
      )}
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-0.5">
        {/* Label + count — hidden in embedded mode */}
        {!embedded && (
          <div className="flex items-center gap-1.5 mr-1">
            <SlidersHorizontal size={13} style={{ color: 'var(--vz-text-secondary)' }} />
            <span className="font-montserrat text-[10px] uppercase tracking-wider hidden sm:inline" style={{ color: 'var(--vz-text-secondary)' }}>Filtros</span>
            {activeCount > 0 && (
              <Badge className="h-4 text-[9px] px-1.5" style={{ background: 'rgba(0,200,83,0.2)', color: '#00C853', border: '1px solid rgba(0,200,83,0.3)' }}>
                {activeCount}
              </Badge>
            )}
          </div>
        )}

        {/* Year quick-select */}
        <div className="flex items-center gap-1 mr-1">
          {YEAR_OPTS.map((y) => {
            const active = activeYear?.label === y.label;
            return (
              <button
                key={y.label}
                onClick={() => selectYear(y)}
                data-testid={`year-btn-${y.label}`}
                className="h-7 px-2.5 text-xs font-semibold rounded-lg transition-all"
                style={{
                  background: active ? 'rgba(74,158,255,0.22)' : 'rgba(74,158,255,0.06)',
                  border: `1px solid ${active ? 'rgba(74,158,255,0.6)' : 'rgba(74,158,255,0.18)'}`,
                  color: active ? '#4A9EFF' : 'var(--vz-text-secondary)',
                  fontWeight: active ? 700 : 500,
                }}
              >
                {y.label}
              </button>
            );
          })}
          {isSyncing && (
            <span
              className="flex items-center gap-1 h-7 px-2 rounded-lg text-[11px] font-medium"
              style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.35)', color: '#F59E0B' }}
              title="Sincronización con ArcGIS en curso — los totales pueden variar unos minutos hasta que termine"
              data-testid="sync-in-progress-badge"
            >
              <RefreshCw size={11} className="animate-spin" /> Sincronizando
            </span>
          )}
        </div>

        {/* Date Range */}
        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 text-xs font-normal px-3"
              style={{
                background: (desde || hasta) ? 'rgba(0,200,83,0.1)' : 'rgba(74,158,255,0.06)',
                border: `1px solid ${(desde || hasta) ? 'rgba(0,200,83,0.3)' : 'rgba(74,158,255,0.2)'}`,
                color: (desde || hasta) ? '#00C853' : 'var(--vz-text-secondary)',
              }}
              data-testid="global-filter-fecha"
            >
              <CalendarIcon size={11} />
              {formatDateRange()}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" style={{ background: 'var(--vz-bg-card)', borderColor: 'var(--vz-border-subtle)', zIndex: 100 }}>
            <Calendar mode="range" selected={range} onSelect={handleDateSelect} numberOfMonths={2} />
            <div className="p-2 border-t flex gap-2" style={{ borderColor: 'var(--vz-border-subtle)' }}>
              {['7d', '30d', '90d', '1y'].map((p) => {
                const days = { '7d': 7, '30d': 30, '90d': 90, '1y': 365 }[p];
                return (
                  <button
                    key={p}
                    onClick={() => {
                      const from = new Date(); from.setDate(from.getDate() - days);
                      handleDateSelect({ from, to: new Date() });
                    }}
                    className="flex-1 py-1 text-xs rounded-lg transition-colors hover:opacity-80"
                    style={{ background: 'rgba(74,158,255,0.1)', color: 'var(--vz-blue)' }}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                onClick={() => {
                  setRange({}); setFilter('desde', null); setFilter('hasta', null); setCalendarOpen(false);
                }}
                className="px-2 py-1 text-xs rounded-lg"
                style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--vz-text-muted)' }}
              >
                Todo
              </button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Main filters — all searchable */}
        <SearchableFilter value={sede} field="sede" placeholder="Sede" options={sedes} testId="global-filter-sede" openKey="sede" {...filterProps} />
        <SearchableFilter value={negocio} field="negocio" placeholder="Negocio" options={negocios} testId="global-filter-negocio" openKey="negocio" {...filterProps} />
        <SearchableFilter value={gerente} field="gerente" placeholder="Gerente" options={gerentes} testId="global-filter-gerente" openKey="gerente" {...filterProps} />

        {/* Toggle extra filters */}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs px-2"
          style={{ color: showExtra ? 'var(--vz-blue)' : 'var(--vz-text-muted)' }}
          onClick={() => setShowExtra(!showExtra)}
        >
          {showExtra ? 'Menos filtros' : '+ Más filtros'}
        </Button>

        {/* Clear */}
        {activeCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { clearFilters(); setRange({}); }}
            className="h-7 text-xs gap-1.5 ml-auto"
            style={{ color: 'var(--vz-text-muted)' }}
            data-testid="clear-filters-button"
          >
            <X size={11} />
            Limpiar
          </Button>
        )}
      </div>

      {/* Extra filters */}
      {showExtra && (
        <div className="flex flex-wrap items-center gap-2 mt-2 pt-2" style={{ borderTop: '1px dashed rgba(74,158,255,0.1)' }}>
          <SearchableFilter value={categoria} field="categoria" placeholder="Categoría" options={categorias} testId="global-filter-categoria" openKey="categoria" {...filterProps} />
          <SearchableFilter value={delegacion} field="delegacion" placeholder="Delegación" options={delegaciones} testId="global-filter-delegacion" openKey="delegacion" {...filterProps} />
          <SearchableFilter value={centro} field="centro" placeholder="Centro" options={centros} testId="global-filter-centro" openKey="centro" {...filterProps} />

          {/* Persona search */}
          <div className="relative flex items-center">
            <Search size={11} className="absolute left-2.5" style={{ color: 'var(--vz-text-muted)' }} />
            <Input
              value={persona || ''}
              onChange={(e) => setFilter('persona', e.target.value || null)}
              placeholder="Buscar persona..."
              className="h-7 pl-7 text-xs w-40"
              style={{
                background: persona ? 'rgba(0,200,83,0.1)' : 'rgba(74,158,255,0.06)',
                border: `1px solid ${persona ? 'rgba(0,200,83,0.3)' : 'rgba(74,158,255,0.2)'}`,
                color: persona ? '#00C853' : 'var(--vz-text-secondary)',
              }}
              data-testid="global-filter-persona"
            />
          </div>

          {/* Incumplimiento quick filter */}
          <div className="flex gap-1">
            {['SI', 'NO'].map((v) => {
              const isActive = incumplimiento === v;
              return (
                <button
                  key={v}
                  onClick={() => setFilter('incumplimiento', isActive ? null : v)}
                  className="h-7 px-3 text-xs rounded-lg font-medium transition-all"
                  style={{
                    background: v === 'SI'
                      ? (isActive ? 'rgba(255,68,68,0.25)' : 'rgba(255,68,68,0.1)')
                      : (isActive ? 'rgba(0,200,83,0.25)' : 'rgba(0,200,83,0.1)'),
                    border: `1px solid ${v === 'SI' ? 'rgba(255,68,68,0.3)' : 'rgba(0,200,83,0.3)'}`,
                    color: v === 'SI' ? '#FF4444' : '#00C853',
                    fontWeight: isActive ? 700 : 500,
                  }}
                  data-testid={`filter-incumplimiento-${v.toLowerCase()}`}
                >
                  Incumpl. {v}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalFilterBar;
